import { LoggingService } from "../loggingSvc";
import { Prerequisite, Bundle, PrerequisiteLoadingService } from "../loading/prerequisite-loading-service";
import { FeatureReleaseNoteConfig } from "../release-notes-config/release-notes-config";
import { FeatureIdentifier } from "../feature-config/feature-config";
import { HttpTrackingConfig } from "../../../ShellCommon/logger/http-interceptor";

declare let markdownit: any;
declare let markdownitInlineComments: any;

export type FeatureFilePaths = { splashPath: string; fullPath: string; imagePaths: string[] };
export class ReleaseNotesBundleService {
  public releaseNoteBundleFiles: string[] = [];
  public featureJsonBundleFiles: string[] = [];
  private mdConverter: any;

  // @ngInject
  constructor(
    private $q: ng.IQService,
    private $http: ng.IHttpService,
    private loggingService: LoggingService,
    private $window: ng.IWindowService
  ) {
    this.mdConverter = new markdownit({ typographer: true, linkify: true }).use(markdownitInlineComments);
  }

  /**
   * @param prerequisite the prerequisite containing the bundle release note paths
   * This method is called upon completion of the prerequisite loader
   */
  public prerequisiteLoadCallback(prerequisite: Prerequisite) {
    let preReleaseNotes = false;
    // skip loading all the bundle on all the pages, but the home and setting page.
    if (
      this.$window.location.hash.toLocaleLowerCase().includes("#/home") ||
      this.$window.location.hash.toLocaleLowerCase().includes("#/settings")
    ) {
      preReleaseNotes = true;
    }
    if (prerequisite.releaseNotesBundle && preReleaseNotes) {
      this.releaseNoteBundleFiles = this.getAllBundleFiles(prerequisite.releaseNotesBundle);
    }

    if (prerequisite.featureJsonBundle && preReleaseNotes) {
      this.featureJsonBundleFiles = this.getAllBundleFiles(prerequisite.featureJsonBundle);
    }
    return this.$q.resolve();
  }

  public getAllFeatures(): ng.IPromise<FeatureReleaseNoteConfig> {
    let config: HttpTrackingConfig = {
      dependencyName: "ServiceDesk.components",
      dependencyOperationName: "ReleaseNotes.GetAllFeatures",
      operationName: "GetAllFeatures"
    };
    return this.$q.all(this.featureJsonBundleFiles.map((featureFile) => this.$http.get(featureFile, config))).then(
      (
        featureFileResponses: ng.IHttpPromiseCallbackArg<{
          name: string;
          features: { [featureId: string]: string };
        }>[]
      ) => {
        let features: FeatureReleaseNoteConfig = {};
        const today: Date = new Date();

        featureFileResponses.forEach(
          (response: ng.IHttpPromiseCallbackArg<{ name: string; features: { [featureId: string]: string } }>) => {
            if (response.status !== 200) {
              this.loggingService.logError(`Unable to retrieve released features.`);
              return;
            }

            const fileData = response.data;

            const app: string = fileData.name;

            if (!features[app]) {
              features[app] = {};
            }
            if (fileData && fileData.features) {
              Object.keys(fileData.features).forEach((featureId: string) => {
                const featureReleaseDate: Date = new Date(fileData.features[featureId]);

                if (featureReleaseDate <= today) {
                  features[app][featureId] = featureReleaseDate;
                }
              });
            }
          }
        );

        return features;
      }
    );
  }

  /**
   * @param featureId the name of the feature to get paths to md files of
   * @return two paths to the splash and detailed view of the md files
   */
  public getFilePathsByFeatureIdentifier(featureId: FeatureIdentifier): FeatureFilePaths {
    let filePaths: FeatureFilePaths = { splashPath: "", fullPath: "", imagePaths: [] };
    if (this.releaseNoteBundleFiles) {
      // if release notes folder is not defined in the app, there will be no files to bundle
      this.releaseNoteBundleFiles.forEach((bundleFile: string) => {
        const file: string = bundleFile.split("/").pop();
        if (file.startsWith(featureId)) {
          if (file.endsWith(".md")) {
            if (file.includes("detail")) {
              filePaths.fullPath = bundleFile;
            } else {
              filePaths.splashPath = bundleFile;
            }
          } else if (file.toLowerCase().endsWith(".png")) {
            filePaths.imagePaths.push(bundleFile);
          } else {
            this.loggingService.logError(`Unsupported file: ${file}`);
          }
        }
      });
    }
    return filePaths;
  }

  public convertMdToHtml(pathToMd: string, pathToImage?: string[]): ng.IPromise<string> {
    return this.getContentsOfFileAsString(pathToMd, pathToImage).then((fileContents) => {
      if (fileContents) {
        return fileContents;
      }
      this.loggingService.logError(`File content for ${pathToMd} is empty.`);
      return "";
    });
  }

  private getContentsOfFileAsString(path: string, pathToImage: string[]): angular.IPromise<string | void> {
    if (!path) {
      this.loggingService.logError("No path provided to get content", "ReleaseNotesBundleService");
      return this.$q.resolve("");
    }
    let config: HttpTrackingConfig = {
      dependencyName: "ServiceDesk.components",
      dependencyOperationName: "ReleaseNotes.GetContents",
      operationName: "GetContents"
    };
    return this.$http
      .get(path, config)
      .then((response) => {
        if (response.status == 200) {
          let markdownString: string = response.data + "";
          if (pathToImage && pathToImage.length > 0) {
            markdownString = this.replaceImagePathInMarkdown(markdownString, pathToImage);
          }
          const transformedContents: string = this.mdConverter.render(markdownString);
          return transformedContents;
        }
      })
      .catch((error) => {
        this.loggingService.logError(`Unable to retrieve file contents for path ${path}: ${error}`);
      });
  }

  private replaceImagePathInMarkdown(markdownString: string, pathToImage: string[]): string {
    pathToImage.forEach((path) => {
      const file = path.split("/").pop();
      // search with case insensitive and global flags
      const searchRegex = new RegExp(file, "ig");
      markdownString = markdownString.replace(searchRegex, path);
    });
    return markdownString;
  }

  private getAllBundleFiles(bundles: Bundle[]): string[] {
    return bundles
      .filter((bundle: Bundle) => bundle.files)
      .map((bundle: Bundle) => bundle.files)
      .reduce((flatArray: string[], bundleFiles: string[]) => flatArray.concat(bundleFiles), []);
  }
}
